import { computed } from 'vue'
import { AptosTokenTransactionExecutor } from '../transaction/transactionExecutor/AptosTokenTransactionExecutor'
import {
  useTransactionAPIStore,
  useWalletStore,
  useCoinStore,
  useAccountTokenAPIStore,
  useAccountBalanceAPIStore,
  useCoinPriceAPIStore,
} from '@/stores'
import {
  type Maybe,
  Wallet,
  type BaseAccount,
  type EncUVD,
  Account,
  Token,
  CoinAmount,
  Address,
  type ISendExtraData,
  type ITransactionExecutor,
  type IPrepareData,
  EthereumTokenTransactionExecutor,
  SolanaTokenTransactionExecutor,
  NearTokenTransactionExecutor,
} from '@/types'
import type { AccountTokenOnChainState } from '@/types/account/BaseAccount'
import type { IAccountToken } from '@/types/api/accountToken'

// V2 AccountToken 상태 타입
type AccountTokenState = 'ACTIVE' | 'INACTIVE' | 'PENDING' | string

class AccountToken implements BaseAccount {
  id: string
  accountId: string
  tokenId: number | null
  state?: AccountTokenState
  onchainState?: AccountTokenOnChainState
  createdTime?: string
  label: string
  account: Maybe<Account>
  coin: Maybe<Token>
  amount!: CoinAmount
  percent?: string
  private _transactionExecutor: Maybe<ITransactionExecutor>
  currencyId?: string
  currency?: any
  balance?: string
  parentAccountId?: string
  walletId?: string

  constructor(accountToken: IAccountToken) {
    this.id = accountToken.id
    this.accountId = accountToken.accountId || accountToken.parentAccountId
    this.tokenId = accountToken.tokenId ?? null
    this.state = accountToken.state
    this.onchainState = accountToken.onchainState as AccountTokenOnChainState
    this.createdTime = accountToken.createdTime || accountToken.createdAt
    this.label = accountToken.label

    this.currencyId = accountToken.currencyId
    this.currency = accountToken.currency
    this.balance = accountToken.balance
    this.parentAccountId = accountToken.parentAccountId
    this.walletId = accountToken.walletId

    const walletStore = useWalletStore()
    const computedAccount = computed(() => {
      return walletStore.getAccount({ accountId: this.accountId })
    })
    Object.defineProperty(this, 'account', {
      get(): Maybe<Account> {
        return computedAccount.value
      },
    })
    const coinStore = useCoinStore()
    const coinPriceAPIStore = useCoinPriceAPIStore()
    const computedCoin = computed(() => {
      // currency 객체가 있으면 이를 Token 형식으로 변환
      if (this.currency) {
        const coinGeckoId = this.currency.cgkId

        // coinPriceList를 직접 참조해야 reactive 추적됨
        const priceInfo = coinGeckoId
          ? coinPriceAPIStore.coinPriceList.find((p) => p.id === coinGeckoId)
          : undefined

        return {
          id: null,
          name: this.currency.name,
          symbol: this.currency.ticker,
          network: this.currencyId,
          decimals: this.currency.decimals,
          coinGeckoId: coinGeckoId,
          iconUrl: this.currency.iconUrl,
          contract: this.currency.contractAddress,
          isTestnet: false,
          coin: this.account?.coin,
          priceInfo: priceInfo,
        } as any
      }
      return coinStore.tokenList.find((token) => token.id === this.tokenId)
    })
    Object.defineProperty(this, 'coin', {
      get(): Maybe<Token> {
        return computedCoin.value
      },
    })
    const accountBalanceStore = useAccountBalanceAPIStore()
    const computedAmount = computed(() => {
      // accountTokenBalanceList에 값이 있으면 우선 사용 (새로고침 후 업데이트된 값)
      const updatedBalance =
        accountBalanceStore.accountTokenBalanceList[this.accountId]?.[this.id]
      const currentBalance = updatedBalance ?? this.balance ?? '0'

      return new CoinAmount({
        amount: currentBalance,
        decimal: this.coin?.decimals ?? 0,
      })
    })
    Object.defineProperty(this, 'amount', {
      get(): CoinAmount {
        return computedAmount.value
      },
    })
  }
  get wallet(): Maybe<Wallet> {
    return this.account?.wallet
  }
  get address(): Address {
    return this.account?.address ?? new Address('')
  }
  get transactionExecutor(): Maybe<ITransactionExecutor> {
    if (!this._transactionExecutor) {
      switch (this.coin?.coin?.coreNetwork) {
        case 'ETHEREUM':
        case 'KLAYTN':
          this._transactionExecutor = new EthereumTokenTransactionExecutor({
            accountToken: this,
          })
          break
        case 'SOLANA':
          this._transactionExecutor = new SolanaTokenTransactionExecutor({
            accountToken: this,
          })
          break
        case 'NEAR':
          this._transactionExecutor = new NearTokenTransactionExecutor({
            accountToken: this,
          })
          break
        case 'APTOS':
          this._transactionExecutor = new AptosTokenTransactionExecutor({
            accountToken: this,
          })
          break
      }
    }
    return this._transactionExecutor
  }

  async fetchBalance(force: boolean | undefined = false): Promise<void> {
    const accountBalanceStore = useAccountBalanceAPIStore()
    await accountBalanceStore.fetchAccountBalance({
      accountId: this.accountId,
      force,
    })
  }

  public getKey(): string {
    return 'accountToken' + this.id
  }
  async setLabel(label: string): Promise<void> {
    const accountTokenAPIStore = useAccountTokenAPIStore()
    const body = {
      label: label,
    }
    await accountTokenAPIStore.updateToken(this.id, body)
  }
  async hideAccount(skipRefresh = false): Promise<void> {
    const accountTokenAPIStore = useAccountTokenAPIStore()
    await accountTokenAPIStore.deleteToken(this.id, this.walletId, skipRefresh)
  }
  async fetchTransactionList(
    force?: boolean,
    refresh?: boolean,
  ): Promise<void> {
    const txAPIStore = useTransactionAPIStore()
    const params = {
      accountId: this.accountId,
      accountTokenId: this.id,
      contract: this.coin?.contract as string,
      force: force,
      refresh: refresh,
    }
    await txAPIStore.fetchAccountTokenTransactionList(params)
  }

  public async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<IPrepareData> {
    if (!this.transactionExecutor) {
      throw new Error('transactionExecutor is not exist')
    }
    return this.transactionExecutor.getPrepareData({
      to: params.to,
      amount: params.amount,
    })
  }
  async send(params: {
    to: string
    amount: string
    extraData: ISendExtraData
    encUVD: EncUVD
    otpCode?: { code: string; isRecoveryCode?: boolean }
  }) {
    if (!this.transactionExecutor) {
      throw new Error('transactionExecutor is not exist')
    }
    const signResponse = await this.transactionExecutor.sign({
      to: params.to,
      amount: params.amount,
      extraData: params.extraData,
      encUVD: params.encUVD,
      otpCode: params.otpCode,
    })
    return this.transactionExecutor.broadcast({ signResponse })
  }
}

class AccountTokenFactory {
  private static instance: AccountTokenFactory
  private constructor() {}

  public static getInstance(): AccountTokenFactory {
    if (!AccountTokenFactory.instance) {
      AccountTokenFactory.instance = new AccountTokenFactory()
    }
    return AccountTokenFactory.instance
  }

  makeAccountToken(accountToken: IAccountToken) {
    const accountTokenObj = new AccountToken(accountToken)
    return accountTokenObj
  }
}

export { AccountToken, AccountTokenFactory }
